import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Product } from '@ridango/core';

import { Observable } from 'rxjs';

import { Feature, FeatureCollection, GeocodeAddress, GeocodeResponse, StopData } from './ride-planner.model';

@Injectable()
export class RidePlannerService {
    constructor(@Inject('ENV') private env: any, private http: HttpClient) {}

    fetchSuggestions(term: string): Observable<FeatureCollection> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/geocode/autocomplete`;
        const options = {
            params: new HttpParams().set('text', term).set('filter', `countrycode:${this.env.COUNTRY_CODE}`),
        };

        return this.http.get<FeatureCollection>(url, options);
    }

    fetchEstimatedPrice(startDate: Date, payload: any) {
        const s = new Date(startDate).toISOString();
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/order/products?start_time=${s}`;

        return this.http.post(url, payload);
    }

    fetchProducts(): Observable<Product[]> {
        const url = `${this.env.API_GATEWAY_URL}/core/customer/v2/region/${this.env.REGION_ID}/ondemand/order/products/all`;

        return this.http.get<Product[]>(url);
    }

    getAddressByCoordinates(lat: number, lon: number) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/geocode`;
        const latlng = `${lat},${lon}`;
        const options = {
            params: new HttpParams().set('text', latlng).set('filter', `countrycode:${this.env.COUNTRY_CODE}`),
        };

        return this.http.get<FeatureCollection>(url, options);
    }

    postBookingFlow(payload: any) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/order`;

        return this.http.post(url, payload);
    }

    fetchLocationSuggestions(term: string, countrycode: string = 'fi'): Observable<FeatureCollection> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/geocode`;
        const options = {
            params: new HttpParams().set('text', term).set('filter', 'countrycode:' + countrycode),
        };

        return this.http.get<FeatureCollection>(url, options);
    }

    fetchLocationSuggestionsEstonia(term: string, includeBusStops: boolean): Observable<GeocodeResponse> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/est/geocode`;
        const options = {
            params: new HttpParams().set('address', term).set('include_bus_stops', String(includeBusStops)),
        };

        return this.http.get<GeocodeResponse>(url, options);
    }

    fetchPotentialStops(): Observable<StopData[]> {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ondemand/stops/predefined?address=&stop_name=`;
        return this.http.get<StopData[]>(url);
    }

    fetchFilteredPotentialStops(input: string): Observable<StopData[]> {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ondemand/stops/predefined?address=${input}&stop_name=${input}`;
        return this.http.get<StopData[]>(url);
    }

    fetchGeocodeAddressByCoordinatesEstonia(lat: number, lng: number): Observable<GeocodeResponse> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/est/geocode/reverse`;

        const options = {
            params: new HttpParams().set('lng', String(lng)).set('lat', String(lat)),
        };

        return this.http.get<GeocodeResponse>(url, options);
    }

    fetchGeocodeAddressByCoordinates(lat: number, lng: number): Observable<FeatureCollection> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/ondemand/routing/geocode`;

        const options = {
            params: new HttpParams().set('text', String(lat) + ',' + String(lng)),
        };

        return this.http.get<FeatureCollection>(url, options);
    }

    mapFeaturesToGeocodeAddresses(features: Feature[]): GeocodeAddress[] {
        return features.map((feature) => {
            return {
                id: feature.properties.place_id,
                address: feature.properties.formatted,
                type: feature.type,
                lat: feature.properties.lat,
                lon: feature.properties.lon,
            };
        });
    }

    linkOrderWithShoppingCart(bookingId, cartId): Observable<any> {
        const url = `${this.env.API_GATEWAY_URL}/core/customer/v2/region/${this.env.REGION_ID}/ondemand/order/${bookingId}/shopping_cart/${cartId}`;
        return this.http.put(url, {});
    }
}
